import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Display,
	Flex,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	rem,
	Spacer,
	useBreakpointValue,
	useDisclosure,
} from '@bonusly/design-system';

import { useGratitudeFeedback } from '../../../gratitude-gauge/scorebox-components';
import { BonusVia, GiveBoxAttachment } from '../../events';
import { useBudget } from '../../hooks/use-budget';
import { useGivePointsCapabilities } from '../../hooks/use-give-points-capabilities';
import { useProxyGiving } from '../../hooks/use-proxy-giving';
import { clearDraft } from '../../utils/recognition-storage';
import { AmountChip } from '../amount-chip';
import { FlashPointsChip } from '../flash-points-chip';
import { GiveModalConfirmDelete } from './give-modal-confirm-delete';
import { GiveModalContent, GiveModalContentHandle } from './give-modal-content';

interface GiveModalProps {
	content?: any;
	initialAttachment?: GiveBoxAttachment;
	/** Presets the gift-wrap toggle from Bizy's "Edit in Givebox" hand-off. */
	initialWrapPost?: boolean;
	/**
	 * Bizy-drafted text handed off by the contextual `/` flow. GiveModalContent
	 * inserts the text with the AI-touched highlight once the modal has mounted
	 * (see HABITS-1294).
	 */
	pendingAiDraft?: { text: string; autoSend?: boolean };
	/**
	 * Remount key for GiveModalContent so draft-assist / via-clear state cannot
	 * leak across give-box open sessions (Chakra may keep the modal tree mounted).
	 */
	sessionKey?: number;
	isOpen: boolean;
	onSuccess: VoidFunction;
	onClose: VoidFunction;
	/** Bizy hand-off channel from the open event ("bizy"), or undefined. */
	via?: BonusVia;
	/** The surface's own channel (e.g. "ms_teams"); recorded when Bizy isn't credited. */
	baseVia?: BonusVia;
	/** Whether the Bizy hand-off carried message body content (seeds start-over detection). */
	bizyHandOffHadContent?: boolean;
	motionPreset?: 'slideInBottom' | 'slideInRight' | 'slideInLeft' | 'slideInTop' | 'scale' | 'none';
	portalProps?: any;
	transparentOverlay?: boolean;
}

export const GiveModal: FC<GiveModalProps> = ({
	content,
	initialAttachment,
	initialWrapPost,
	pendingAiDraft,
	sessionKey = 0,
	isOpen,
	onClose,
	onSuccess,
	via,
	baseVia,
	bizyHandOffHadContent,
	motionPreset = 'slideInBottom',
	transparentOverlay = false,
}) => {
	const { t } = useTranslation(['common', 'recognition']);
	const budget = useBudget();
	const proxyGiving = useProxyGiving();
	const { canGivePoints: showPointsChips, canGiveAmount: showAmountChip } = useGivePointsCapabilities();
	const amountChipBalance = proxyGiving.selectedPrincipal ? proxyGiving.selectedPrincipal.givingBalance : (budget.monthlyAllowanceBalance ?? 0);
	const giveModalContentRef = useRef<GiveModalContentHandle>(null);
	const isOverlayClickRef = useRef(false);
	// Set to the intended outcome before onClose() is called so the cleanup useEffect in
	// GiveModalContent can read it during unmount (the ref is null by onCloseComplete time).
	const pendingOutcomeRef = useRef<'submitted' | 'discarded' | 'abandoned'>('abandoned');
	const [hasVideoAttachment, setHasVideoAttachment] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isOpen: isConfirmModalOpen, onClose: onConfirmModalClose, onOpen: onConfirmDeleteVideoModalOpen } = useDisclosure();
	const { isOpen: isDiscardConfirmOpen, onClose: onDiscardConfirmClose, onOpen: onDiscardConfirmOpen } = useDisclosure();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const resetGratitudeFeedback = useGratitudeFeedback((state) => state.reset);

	// Save draft only when clicking outside the modal (accidental dismissal)
	const handleOverlayClick = () => {
		isOverlayClickRef.current = true;
		giveModalContentRef.current?.saveDraft();
	};

	// Bizy contextual `/` hands off the session to the side panel and later
	// re-opens the modal via `GiveBoxEvent.Open` with the drafted text.
	// Treat this like an overlay-click close (save draft, suppress
	// `clearDraft`) so recipients / hashtags / amount survive the hand-off.
	const handleCloseForBizy = () => {
		isOverlayClickRef.current = true;
		giveModalContentRef.current?.saveDraft();
		onClose();
	};

	const handleOnClose = () => {
		// If the user tries to close the modal after attaching a video,
		// we need to let them know doing so will delete the video.
		if (hasVideoAttachment) {
			onConfirmDeleteVideoModalOpen();
		} else if (!isOverlayClickRef.current && giveModalContentRef.current?.hasUserText()) {
			onDiscardConfirmOpen();
		} else {
			// Overlay clicks are accidental dismissals (abandoned); X-button clicks are intentional (discarded).
			pendingOutcomeRef.current = isOverlayClickRef.current ? 'abandoned' : 'discarded';
			if (pendingOutcomeRef.current === 'discarded') {
				clearDraft();
			}
			onClose();
		}
	};

	// Stamp the outcome before delegating so handleCloseComplete can clear the draft
	// on success even when the caller doesn't go through useGiveModal.
	const handleOnSuccess = () => {
		pendingOutcomeRef.current = 'submitted';
		onSuccess();
	};

	// Clear draft when the modal finishes closing for every resolved outcome
	// (submitted or explicitly discarded). Abandoned (overlay click) preserves
	// the draft so it can be restored on the next open.
	const handleCloseComplete = () => {
		resetGratitudeFeedback();
		if (pendingOutcomeRef.current === 'discarded' || pendingOutcomeRef.current === 'submitted') {
			clearDraft();
		}
		// Reset for the next open/close cycle.
		pendingOutcomeRef.current = 'abandoned';
		isOverlayClickRef.current = false;
	};

	const handleConfirmDelete = () => {
		onConfirmModalClose();
		// Respect the original close intent: overlay clicks are accidental (abandoned),
		// X-button clicks are intentional (discarded). This matters because handleOverlayClick
		// already saved the draft, and we must not clear it on an accidental dismissal.
		pendingOutcomeRef.current = isOverlayClickRef.current ? 'abandoned' : 'discarded';
		if (pendingOutcomeRef.current === 'discarded') {
			clearDraft();
		}
		onClose();
		setHasVideoAttachment(false);
	};

	const handleConfirmDiscard = () => {
		onDiscardConfirmClose();
		pendingOutcomeRef.current = 'discarded';
		clearDraft();
		onClose();
	};

	const mobileAttributes = {
		height: 'auto',
		minHeight: '100vh',
		m: 0,
		overflowX: 'hidden' as const,
		sx: {
			'@supports (-webkit-touch-callout: none)': {
				height: 'auto',
				minHeight: '-webkit-fill-available',
				maxHeight: 'none',
			},
		},
	};
	const desktopAttributes = {
		maxH: 'fit-content',
	};

	const mobileModalHeader = (
		<ModalHeader p={5} position="sticky" top={0} zIndex={1} bg="bg.surface" height={rem(80)} borderBottom="none" borderColor="transparent">
			<Flex alignItems="center" justifyContent="space-between" height="100%">
				<ModalCloseButton position="unset" fontSize="md" fontWeight="medium" px={10} py={5} minW={16} bg="transparent" color="secondary.fg">
					{t('cancel', { ns: 'common' })}
				</ModalCloseButton>
				<Display textAlign="center" fontSize="lg" fontWeight="semibold">
					{t('bonus_input.mobile_modal_header', { ns: 'recognition' })}
				</Display>
				<Box minW={16} textAlign="right"></Box>
			</Flex>
		</ModalHeader>
	);

	const desktopModalHeader = (
		<ModalHeader px={6} pt={5} pb={rem(4)} borderBottom="none">
			<Flex pr={0} alignItems="center">
				{t('bonus_input.modal_header_redesign', { ns: 'recognition' })}
				<Spacer />
				{showPointsChips && <FlashPointsChip />}
				{showAmountChip && <AmountChip amount={amountChipBalance} h={8} />}
				<ModalCloseButton borderRadius="full" position="static" size="md" top={0} right={0} ml={2} />
			</Flex>
		</ModalHeader>
	);

	const getHeader = () => {
		if (isMobile) {
			return mobileModalHeader;
		}
		return desktopModalHeader;
	};

	return (
		<>
			<Modal
				data-testid="give-modal"
				isOpen={isOpen}
				onClose={handleOnClose}
				onCloseComplete={handleCloseComplete}
				onOverlayClick={handleOverlayClick}
				scrollBehavior="outside"
				size={isMobile ? 'full' : 'xl'}
				motionPreset={motionPreset}
				// Should we disable the overlay click all the time?
				closeOnOverlayClick={!isSubmitting}
			>
				<ModalOverlay
					{...(transparentOverlay ? { bg: 'transparent' } : {})} // Temp Fix for Safari: Ensures modal overlay displays correctly when navigation bar is hidden
					{...(isMobile ? { minH: '200%', bg: 'bg.surface' } : {})} // Fix for IOS Keyboard resizing which hides a brief flash of the bonus feed when the keyboard opens
				/>
				<ModalContent {...(isMobile ? mobileAttributes : desktopAttributes)}>
					{getHeader()}
					<GiveModalContent
						key={sessionKey}
						ref={giveModalContentRef}
						content={content}
						initialAttachment={initialAttachment}
						initialWrapPost={initialWrapPost}
						pendingAiDraft={pendingAiDraft}
						onCloseForBizy={handleCloseForBizy}
						onSuccess={handleOnSuccess}
						proxyGiving={proxyGiving}
						setIsSubmitting={setIsSubmitting}
						onVideoAttachmentChange={setHasVideoAttachment}
						via={via}
						baseVia={baseVia}
						bizyHandOffHadContent={bizyHandOffHadContent}
						pendingOutcomeRef={pendingOutcomeRef}
					/>
					{isSubmitting && <Box bgColor="whiteAlpha.700" rounded="md" cursor="not-allowed" inset={0} position="absolute" />}
				</ModalContent>
			</Modal>
			<GiveModalConfirmDelete isOpen={isConfirmModalOpen} onClose={onConfirmModalClose} onConfirm={handleConfirmDelete} />
			<GiveModalConfirmDelete
				isOpen={isDiscardConfirmOpen}
				onClose={onDiscardConfirmClose}
				onConfirm={handleConfirmDiscard}
				heading={t('bonus_input.close_confirm.header', { ns: 'recognition' })}
				body={t('bonus_input.close_confirm.description', { ns: 'recognition' })}
				cancelText={t('bonus_input.close_confirm.keep_editing', { ns: 'recognition' })}
				confirmText={t('discard', { ns: 'common' })}
			/>
		</>
	);
};
